import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import CalendarIcon from '../../assets/icons/calendar.svg?react';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import './DateTimeInput.css';

/**
 * DateTimeInput 컴포넌트
 * @param {Object} props
 * @param {'date' | 'datetime'} props.type - 'date': 날짜만, 'datetime': 날짜+시간
 * @param {dayjs.Dayjs | string | null} props.value - 선택된 날짜/시간 (dayjs 객체, ISO 문자열, 또는 null)
 * @param {Function} props.onChange - 값 변경 시 호출 (dayjs 객체를 인자로 받음)
 * @param {dayjs.Dayjs | string | null} props.min - 최소 날짜/시간
 * @param {dayjs.Dayjs | string | null} props.max - 최대 날짜/시간
 * @param {string} props.placeholder - 플레이스홀더 텍스트
 * @param {boolean} props.disabled - 비활성화 여부
 * @param {string} props.className - 추가 CSS 클래스
 * @param {string} props.label - 라벨 텍스트
 * @param {boolean} props.showLabel - 라벨 표시 여부
 */
const DateTimeInput = ({
  type = 'datetime',
  value = null,
  onChange = () => {},
  min = null,
  max = null,
  placeholder = '',
  disabled = false,
  className = '',
  label = '',
  showLabel = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeValue, setTimeValue] = useState({ hour: '00', minute: '00' });
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  // value를 dayjs 객체로 변환
  const valueDayjs = value ? dayjs(value) : null;
  const minDayjs = min ? dayjs(min) : null;
  const maxDayjs = max ? dayjs(max) : null;

  // 초기값 설정
  useEffect(() => {
    if (valueDayjs && valueDayjs.isValid()) {
      setSelectedDate(valueDayjs);
      setCurrentMonth(valueDayjs);
      if (type === 'datetime') {
        setTimeValue({
          hour: valueDayjs.format('HH'),
          minute: valueDayjs.format('mm'),
        });
      }
    } else {
      setSelectedDate(null);
      if (type === 'datetime') {
        setTimeValue({ hour: '00', minute: '00' });
      }
    }
  }, [value, type]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 날짜 포맷팅
  const formatValue = () => {
    if (!valueDayjs || !valueDayjs.isValid()) {
      return placeholder || (type === 'datetime' ? '날짜와 시간을 선택하세요' : '날짜를 선택하세요');
    }
    
    if (type === 'datetime') {
      return valueDayjs.format('YYYY-MM-DD HH:mm');
    } else {
      return valueDayjs.format('YYYY-MM-DD');
    }
  };

  // 달력 데이터 생성
  const generateCalendarData = (month) => {
    const startOfMonth = month.startOf('month');
    const endOfMonth = month.endOf('month');
    const startOfWeek = startOfMonth.startOf('week');
    const endOfWeek = endOfMonth.endOf('week');
    
    const days = [];
    let current = startOfWeek;
    
    while (current.isBefore(endOfWeek) || current.isSame(endOfWeek, 'day')) {
      days.push(current);
      current = current.add(1, 'day');
    }
    
    return days;
  };

  // 날짜 선택 가능 여부 확인
  const isDateDisabled = (date) => {
    if (minDayjs && date.isBefore(minDayjs, 'day')) return true;
    if (maxDayjs && date.isAfter(maxDayjs, 'day')) return true;
    return false;
  };

  // 날짜 클릭 처리
  const handleDateClick = (date) => {
    if (disabled || isDateDisabled(date)) return;

    let newDateTime = date;
    let newTimeValue = { ...timeValue };
    
    // datetime 모드인 경우 시간 처리
    if (type === 'datetime') {
      // min이 있고 같은 날짜인 경우, min의 시간 사용
      if (minDayjs && date.isSame(minDayjs, 'day')) {
        newTimeValue = {
          hour: minDayjs.format('HH'),
          minute: minDayjs.format('mm'),
        };
        newDateTime = date
          .hour(minDayjs.hour())
          .minute(minDayjs.minute())
          .second(0)
          .millisecond(0);
      } else if (selectedDate) {
        // 기존 선택된 날짜가 있으면 기존 시간 유지
        newDateTime = date
          .hour(parseInt(timeValue.hour) || 0)
          .minute(parseInt(timeValue.minute) || 0)
          .second(0)
          .millisecond(0);
      } else {
        // 처음 선택하는 경우 기본 시간 사용
        newDateTime = date
          .hour(parseInt(timeValue.hour) || 0)
          .minute(parseInt(timeValue.minute) || 0)
          .second(0)
          .millisecond(0);
      }
    }

    // min/max 검증
    if (minDayjs && newDateTime.isBefore(minDayjs)) {
      setError(`최소 날짜/시간은 ${minDayjs.format(type === 'datetime' ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD')}입니다.`);
      return;
    }
    if (maxDayjs && newDateTime.isAfter(maxDayjs)) {
      setError(`최대 날짜/시간은 ${maxDayjs.format(type === 'datetime' ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD')}입니다.`);
      return;
    }

    setSelectedDate(date);
    setTimeValue(newTimeValue);
    setError('');
    
    // date 모드인 경우 즉시 적용
    if (type === 'date') {
      onChange(newDateTime);
      setIsOpen(false);
    } else if (type === 'datetime' && minDayjs && date.isSame(minDayjs, 'day')) {
      // 같은 날짜이고 min이 있는 경우 즉시 적용 (시간이 min의 시간으로 설정됨)
      onChange(newDateTime);
    }
  };

  // 시간 변경 처리
  const handleTimeChange = (field, newValue) => {
    const numValue = parseInt(newValue) || 0;
    let updatedTime = { ...timeValue };

    if (field === 'hour') {
      updatedTime.hour = Math.max(0, Math.min(23, numValue)).toString().padStart(2, '0');
    } else if (field === 'minute') {
      updatedTime.minute = Math.max(0, Math.min(59, numValue)).toString().padStart(2, '0');
    }

    setTimeValue(updatedTime);

    // 날짜가 선택된 경우 즉시 업데이트
    if (selectedDate) {
      const newDateTime = selectedDate
        .hour(parseInt(updatedTime.hour))
        .minute(parseInt(updatedTime.minute))
        .second(0)
        .millisecond(0);

      // min/max 검증
      if (minDayjs && newDateTime.isBefore(minDayjs)) {
        setError(`최소 날짜/시간은 ${minDayjs.format('YYYY-MM-DD HH:mm')}입니다.`);
        return;
      }
      if (maxDayjs && newDateTime.isAfter(maxDayjs)) {
        setError(`최대 날짜/시간은 ${maxDayjs.format('YYYY-MM-DD HH:mm')}입니다.`);
        return;
      }

      setError('');
      onChange(newDateTime);
    }
  };

  // 확인 버튼 클릭
  const handleConfirm = () => {
    if (!selectedDate) {
      setError('날짜를 선택해주세요.');
      return;
    }

    let finalDateTime = selectedDate;
    
    if (type === 'datetime') {
      finalDateTime = selectedDate
        .hour(parseInt(timeValue.hour) || 0)
        .minute(parseInt(timeValue.minute) || 0)
        .second(0)
        .millisecond(0);
    }

    // min/max 최종 검증
    if (minDayjs && finalDateTime.isBefore(minDayjs)) {
      setError(`최소 날짜/시간은 ${minDayjs.format(type === 'datetime' ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD')}입니다.`);
      return;
    }
    if (maxDayjs && finalDateTime.isAfter(maxDayjs)) {
      setError(`최대 날짜/시간은 ${maxDayjs.format(type === 'datetime' ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD')}입니다.`);
      return;
    }

    onChange(finalDateTime);
    setIsOpen(false);
    setError('');
  };

  // 취소 버튼 클릭
  const handleCancel = () => {
    setIsOpen(false);
    setError('');
    // 원래 값으로 복원
    if (valueDayjs && valueDayjs.isValid()) {
      setSelectedDate(valueDayjs);
      setCurrentMonth(valueDayjs);
      if (type === 'datetime') {
        setTimeValue({
          hour: valueDayjs.format('HH'),
          minute: valueDayjs.format('mm'),
        });
      }
    } else {
      setSelectedDate(null);
      setTimeValue({ hour: '00', minute: '00' });
    }
  };

  // 오늘 버튼 클릭
  const handleToday = () => {
    const today = dayjs();
    if (isDateDisabled(today)) return;
    
    setCurrentMonth(today);
    setSelectedDate(today);
    
    if (type === 'datetime') {
      setTimeValue({
        hour: today.format('HH'),
        minute: today.format('mm'),
      });
    }
  };

  // 날짜 상태 확인
  const getDateState = (date) => {
    const isCurrentMonth = date.isSame(currentMonth, 'month');
    const isToday = date.isSame(dayjs(), 'day');
    const isSelected = selectedDate && date.isSame(selectedDate, 'day');
    const isDisabled = isDateDisabled(date);

    return {
      isCurrentMonth,
      isToday,
      isSelected,
      isDisabled,
    };
  };

  // 달력 렌더링
  const renderCalendar = () => {
    const days = generateCalendarData(currentMonth);
    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

    return (
      <div className="datetime-calendar-container">
        <div className="datetime-calendar-header">
          <button
            className="datetime-calendar-nav-btn"
            onClick={() => setCurrentMonth(prev => prev.subtract(1, 'month'))}
            type="button"
          >
            <ChevronLeftIcon />
          </button>
          <span className="datetime-calendar-month-year">
            {currentMonth.format('YYYY년 M월')}
          </span>
          <button
            className="datetime-calendar-nav-btn"
            onClick={() => setCurrentMonth(prev => prev.add(1, 'month'))}
            type="button"
          >
            <ChevronLeftIcon style={{ transform: 'rotate(180deg)' }} />
          </button>
        </div>

        <div className="datetime-calendar-weekdays">
          {weekDays.map(day => (
            <div key={day} className="datetime-calendar-weekday">{day}</div>
          ))}
        </div>

        <div className="datetime-calendar-days">
          {days.map((date, index) => {
            const state = getDateState(date);
            return (
              <button
                key={index}
                type="button"
                className={`datetime-calendar-day ${
                  !state.isCurrentMonth ? 'other-month' : ''
                } ${state.isSelected ? 'selected' : ''} ${
                  state.isToday ? 'today' : ''
                } ${state.isDisabled ? 'disabled' : ''}`}
                onClick={() => handleDateClick(date)}
                disabled={state.isDisabled || disabled}
              >
                {date.date()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // 시간 입력 렌더링
  const renderTimeInput = () => {
    if (type !== 'datetime') return null;

    return (
      <div className="datetime-time-container">
        <div className="datetime-time-label">시간</div>
        <div className="datetime-time-inputs">
          <div className="datetime-time-input-group">
            <input
              type="number"
              className="datetime-time-input"
              min="0"
              max="23"
              value={timeValue.hour}
              onChange={(e) => handleTimeChange('hour', e.target.value)}
              disabled={disabled || !selectedDate}
            />
            <span className="datetime-time-separator">:</span>
            <input
              type="number"
              className="datetime-time-input"
              min="0"
              max="59"
              value={timeValue.minute}
              onChange={(e) => handleTimeChange('minute', e.target.value)}
              disabled={disabled || !selectedDate}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`datetime-input-wrapper ${className}`} ref={dropdownRef}>
      {showLabel && label && (
        <label className="datetime-input-label">{label}</label>
      )}
      <div
        className={`datetime-input-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''} ${error ? 'error' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={`datetime-input-text ${!valueDayjs ? 'placeholder' : ''}`}>
          {formatValue()}
        </span>
        <CalendarIcon className="datetime-input-icon" />
      </div>
      {error && (
        <div className="datetime-input-error-message">{error}</div>
      )}
      
      {isOpen && !disabled && (
        <div className="datetime-input-dropdown">
          <div className="datetime-input-content">
            {renderCalendar()}
            {renderTimeInput()}
          </div>
          <div className="datetime-input-actions">
            <button
              type="button"
              className="datetime-input-btn today"
              onClick={handleToday}
            >
              오늘
            </button>
            <div className="datetime-input-actions-right">
              <button
                type="button"
                className="datetime-input-btn cancel"
                onClick={handleCancel}
              >
                취소
              </button>
              <button
                type="button"
                className="datetime-input-btn confirm"
                onClick={handleConfirm}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimeInput;

